import { routes } from './routes';
import { getRouteForRoleOrCategory } from './roleRoutes';

export interface DrawerNavLink {
  id: string;
  label: string;
  to: string;
}

export interface DrawerAccordionSection {
  id: string;
  label: string;
  links: DrawerNavLink[];
}

export interface DrawerRoleChip {
  code: string;
  label: string;
}

/** Role switcher grid — same set as template personal/role registry. */
export const DRAWER_ROLE_SWITCH: DrawerRoleChip[] = [
  { code: 'SYSTEM_OWNER', label: 'Владелец системы' },
  { code: 'ADVISOR', label: 'Эдвайзер' },
  { code: 'SUPERVISOR', label: 'Научный руководитель' },
  { code: 'RESEARCHER', label: 'Исследователь' },
  { code: 'OPERATIONS_PARTNER', label: 'Операционный партнёр' },
  { code: 'RESEARCH_PARTNER', label: 'Научный партнёр / коллега' },
  { code: 'DEPARTMENT_HEAD', label: 'Руководитель подразделения' },
  { code: 'DEPARTMENT_LAB', label: 'Лаборант кафедры' },
  { code: 'DOCTORAL_STUDENT', label: 'Докторант' },
  { code: 'STUDENT', label: 'Обучающийся под руководством' },
  { code: 'TEACHER', label: 'Преподаватель' },
  { code: 'COURSE_STUDENT', label: 'Обучающийся' },
  { code: 'AUTHOR', label: 'Автор / соавтор' },
  { code: 'CLIENT', label: 'Клиент' },
  { code: 'PROJECT_EDITOR', label: 'Редактор проекта' },
  { code: 'PROJECT_MEMBER', label: 'Участник проекта' },
];

/** Human-readable role label for UI chips (never show raw SYSTEM_OWNER etc.). */
export function formatRoleLabel(code: string | null | undefined): string {
  if (!code) return '—';
  return DRAWER_ROLE_SWITCH.find((item) => item.code === code)?.label ?? code;
}
/**
 * Accordion groups from template `navSections`, screens cleaned:
 * no :param detail routes, no English tech labels, paths not shown in UI.
 */
export const getDrawerAccordion = (
  activeRole: string | null | undefined,
): DrawerAccordionSection[] => {
  const owner = !activeRole || activeRole === 'SYSTEM_OWNER';
  const home = routes.home;
  const cabinet = getRouteForRoleOrCategory(activeRole) ?? routes.owner;

  const work: DrawerAccordionSection = {
    id: 'work',
    label: 'Работа',
    links: [
      { id: 'home', label: 'Главная', to: home },
      {
        id: 'requests',
        label: 'Заявки',
        to: owner ? `${routes.owner}/requests` : routes.requests,
      },
      { id: 'new-request', label: 'Новая заявка', to: routes.newRequest },
      { id: 'projects', label: 'Проекты', to: routes.projectMember },
      ...(owner
        ? [
            { id: 'coverage', label: 'Каталог услуг', to: `${routes.owner}/coverage` },
            { id: 'cabinet', label: 'Кабинет владельца', to: routes.owner },
          ]
        : [{ id: 'cabinet', label: 'Мой кабинет', to: cabinet }]),
    ],
  };

  const authors: DrawerAccordionSection = {
    id: 'authors',
    label: 'Авторы',
    links: [
      { id: 'author', label: 'Кабинет автора', to: routes.author },
      { id: 'journals', label: 'Журналы', to: routes.journals },
      ...(owner
        ? [{ id: 'orders', label: 'Заказы и публикации', to: `${routes.owner}/orders` }]
        : []),
    ],
  };

  const education: DrawerAccordionSection = {
    id: 'education',
    label: 'Обучение',
    links: [
      { id: 'doctoral', label: 'Докторанты', to: routes.doctoral },
      { id: 'advisor', label: 'Эдвайзер', to: routes.advisor },
      { id: 'teacher', label: 'Преподаватель', to: routes.teacher },
      { id: 'student', label: 'Обучающийся', to: routes.courseStudent },
      { id: 'edu-portal', label: 'Образование', to: routes.portalSection('education') },
    ],
  };

  const supervision: DrawerAccordionSection = {
    id: 'supervision',
    label: 'Научное руководство',
    links: [
      { id: 'supervised-learners', label: 'Обучающиеся под руководством', to: `${routes.supervisor}/learners` },
      { id: 'supervisor', label: 'Научный руководитель', to: routes.supervisor },
      { id: 'department-head', label: 'Кафедра', to: routes.departmentHead },
      { id: 'department-lab', label: 'Лаборант кафедры', to: routes.departmentLab },
      { id: 'competitions', label: 'Конкурсы', to: routes.portalSection('competitions') },
      { id: 'vacancies', label: 'Вакансии', to: routes.portalSection('vacancies') },
    ],
  };

  const tools: DrawerAccordionSection = {
    id: 'tools',
    label: 'Инструменты',
    links: [
      {
        id: 'research-tools',
        label: 'Научные инструменты',
        to: owner ? `${routes.owner}/tools` : routes.assistant,
      },
      { id: 'assistant', label: 'AI-консультант', to: routes.assistant },
      ...(owner
        ? [
            { id: 'files', label: 'Файлы и версии', to: `${routes.owner}/files` },
            {
              id: 'communications',
              label: 'Коммуникации',
              to: `${routes.owner}/communications`,
            },
            { id: 'content', label: 'Контент-центр', to: `${routes.owner}/content` },
          ]
        : []),
    ],
  };

  const administration: DrawerAccordionSection = {
    id: 'administration',
    label: 'Администрирование',
    links: owner
      ? [
          { id: 'access', label: 'Пользователи и доступы', to: `${routes.owner}/access` },
          { id: 'audit', label: 'Аудит', to: routes.audit },
          { id: 'security', label: 'Безопасность', to: routes.security },
        ]
      : [
          { id: 'people', label: 'Люди', to: routes.people },
          { id: 'security', label: 'Безопасность', to: routes.security },
        ],
  };

  if (!activeRole) {
    return [
      {
        id: 'work',
        label: 'Работа',
        links: [
          { id: 'home', label: 'Главная', to: routes.home },
          { id: 'login', label: 'Войти', to: routes.login },
          { id: 'request', label: 'Запросить доступ', to: routes.requestAccess },
        ],
      },
      {
        id: 'portal',
        label: 'Портал',
        links: [
          { id: 'about', label: 'О нас', to: routes.portalSection('about') },
          { id: 'news', label: 'Новости', to: routes.portalSection('news') },
          { id: 'materials', label: 'Материалы', to: routes.portalSection('materials') },
          { id: 'contacts', label: 'Контакты', to: routes.portalSection('contacts') },
        ],
      },
    ];
  }

  return [work, authors, education, supervision, tools, administration];
};

/** @deprecated */
export const getDrawerNavigation = getDrawerAccordion;
export const drawerNavigation = getDrawerAccordion('SYSTEM_OWNER');
